import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle, ChevronDown, Eye, FileCheck, FileText, Link as LinkIcon, Plus,
  Upload, X,
} from 'lucide-react';
import type { PendingUpload, PaperType, Taxonomy } from './types';
import { TaxonomyPicker } from './TaxonomyPicker';

interface Props {
  open: boolean;
  pending: PendingUpload[];
  taxonomy: Taxonomy | null;
  taxonomyLoading: boolean;
  onClose: () => void;
  onClear: () => void;
  onSubmit: () => void;
  onRemove: (id: string) => void;
  onPatch: (id: string, patch: Partial<PendingUpload>) => void;
  /** Push a new URL-based item onto the pending queue. */
  onAddUrl: (url: string) => void;
  /** Push new File-based items onto the pending queue. */
  onAddFiles: (files: FileList | File[]) => void;
  /** When the parent page scopes this drawer to one kind (Curriculum
   *  or Past-paper page), we hide the per-item Kind toggle so the
   *  trainer can't cross-post. Undefined = show the toggle. */
  lockedKind?: 'exam_paper' | 'curriculum';
}

const PAPER_TYPES: PaperType[] = ['Final', 'CAT', 'Re-sit', 'Mock', 'Other'];

/**
 * Left-sliding review drawer for pending uploads.
 *
 * Trainers can queue papers two ways — dropping local PDFs, or pasting
 * a link to a hosted PDF (Google Drive / Dropbox / institutional site).
 * The drawer opens on the left, docks flush to the viewport edge on
 * mobile, and holds:
 *
 *   • A URL entry row at the top → pushes a new PendingUpload as a
 *     'url' source, which the backend fetches server-side.
 *   • One card per pending item with a Preview button (opens the file
 *     or link in a new tab so the trainer can eyeball it before
 *     shipping to the RAG index).
 *   • The cascading Speciality → Level → Module → Course picker plus
 *     Year / Paper type / Academic session.
 *
 * Submit stays disabled until every card has both the curriculum path
 * and a valid source (file OR reachable URL).
 */
export const PendingSheet: React.FC<Props> = ({
  open, pending, taxonomy, taxonomyLoading, onClose, onClear, onSubmit, onRemove, onPatch, onAddUrl, onAddFiles, lockedKind,
}) => {
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Only one accordion open at a time — defaults to "file" since that's
  // the flow most trainers use. Clicking the closed section header
  // swaps them without collapsing everything.
  const [openSource, setOpenSource] = useState<'file' | 'url'>('file');

  // Memoise preview URLs for local files so we're not creating and
  // leaking new object URLs on every render.
  const previewUrls = useMemo(() => {
    const m = new Map<string, string>();
    pending.forEach((p) => {
      if (p.source === 'file' && p.file) {
        m.set(p.id, URL.createObjectURL(p.file));
      }
    });
    return m;
  }, [pending]);
  React.useEffect(() => {
    return () => { previewUrls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [previewUrls]);

  if (!open || typeof document === 'undefined') return null;

  // Curriculum uploads are scoped to a Speciality → Level — the CDACC
  // unit standard covers every course in that level — so Module/Course
  // aren't required (or even shown) for them.
  const canSubmit = pending.length > 0 && pending.every((p) => {
    const isCurriculum = (p.kind || 'exam_paper') === 'curriculum';
    const taxonomyOk = isCurriculum
      ? !!p.speciality_key && !!p.level_key
      : !!p.speciality_key && !!p.level_key && !!p.module_key && !!p.course_id;
    return taxonomyOk && (p.source === 'file' ? !!p.file : !!p.url);
  });

  const addUrl = () => {
    const raw = urlInput.trim();
    if (!raw) return;
    try {
      const u = new URL(raw);
      if (!/^https?:$/.test(u.protocol)) throw new Error('bad protocol');
      onAddUrl(raw);
      setUrlInput('');
      setUrlError(null);
    } catch {
      setUrlError('Enter a full https:// URL that points to a PDF');
    }
  };

  const openPreview = (p: PendingUpload) => {
    if (p.source === 'file') {
      const url = previewUrls.get(p.id);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } else if (p.url) {
      window.open(p.url, '_blank', 'noopener,noreferrer');
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10000] flex justify-end bg-slate-900/45 backdrop-blur-sm animate-[ecFade_.2s_ease]"
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-[560px] flex-col bg-white shadow-[-20px_0_60px_rgba(0,0,0,0.2)] animate-[ecSlideInRight_.28s_ease]"
        style={{ fontFamily: "'Afacad Flux', system-ui, sans-serif" }}
      >
        {/* ── Head ── */}
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <p className="m-0 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#0e9172]">
              <Upload size={11} /> Ready to upload
            </p>
            <h3 className="m-0 mt-1 text-[22px] leading-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Tag and ship
            </h3>
            <p className="m-0 mt-0.5 text-[12px] text-slate-500">
              {pending.length} item{pending.length === 1 ? '' : 's'} · drop PDFs or paste links
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-900 hover:bg-slate-200"
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Add-item accordion (file drop OR paste link) ── */}
        <div className="border-b border-slate-100 px-5 py-4">
          <p className="mb-3 text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-500">
            Add papers
          </p>
          <div className="flex flex-col gap-2">
            {/* Panel 1 — Upload PDFs */}
            <div
              className={`
                overflow-hidden rounded-2xl border transition
                ${openSource === 'file'
                  ? 'border-[#0e9172]/40 bg-white shadow-[0_1px_2px_rgba(14,145,114,.06)]'
                  : 'border-slate-200 bg-slate-50/70 hover:border-slate-300'}
              `}
            >
              <button
                type="button"
                onClick={() => setOpenSource('file')}
                aria-expanded={openSource === 'file'}
                className={`
                  flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition
                  ${openSource === 'file' ? 'text-slate-900' : 'text-slate-700 hover:text-slate-900'}
                `}
              >
                <span className="flex items-center gap-2.5">
                  <span
                    className={`
                      flex h-9 w-9 items-center justify-center rounded-xl transition
                      ${openSource === 'file'
                        ? 'bg-gradient-to-br from-[#1a5276] to-[#0e9172] text-white shadow-[0_4px_12px_rgba(14,145,114,.28)]'
                        : 'bg-white text-slate-500 ring-1 ring-slate-200'}
                    `}
                  >
                    <Upload size={15} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-bold leading-tight">Upload PDFs</span>
                    <span className="block text-[11px] text-slate-500">
                      Drag & drop or browse — up to 30 MB each
                    </span>
                  </span>
                </span>
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-slate-400 transition-transform ${openSource === 'file' ? 'rotate-180 text-[#0e9172]' : ''}`}
                />
              </button>
              {openSource === 'file' && (
                <div className="border-t border-slate-100 px-4 pb-4 pt-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      if (e.dataTransfer.files?.length) onAddFiles(e.dataTransfer.files);
                    }}
                    className={`
                      w-full rounded-2xl border-2 border-dashed px-4 py-6 text-center transition
                      ${dragOver
                        ? 'border-[#0e9172] bg-[#0e9172]/8 -translate-y-0.5'
                        : 'border-slate-300 bg-white hover:border-[#0e9172] hover:bg-[#0e9172]/5'}
                    `}
                  >
                    <span className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1a5276] to-[#0e9172] text-white">
                      <Upload size={18} />
                    </span>
                    <p
                      className="m-0 mb-0.5 text-[16px] text-slate-900"
                      style={{ fontFamily: "'Instrument Serif', serif" }}
                    >
                      Drop PDFs here, or tap to browse
                    </p>
                    <p className="m-0 text-[11px] text-slate-500">
                      PDF only · 30 MB max per file · multiple files supported
                    </p>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.length) onAddFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Panel 2 — Add by link */}
            <div
              className={`
                overflow-hidden rounded-2xl border transition
                ${openSource === 'url'
                  ? 'border-[#1a5276]/40 bg-white shadow-[0_1px_2px_rgba(26,82,118,.06)]'
                  : 'border-slate-200 bg-slate-50/70 hover:border-slate-300'}
              `}
            >
              <button
                type="button"
                onClick={() => setOpenSource('url')}
                aria-expanded={openSource === 'url'}
                className={`
                  flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition
                  ${openSource === 'url' ? 'text-slate-900' : 'text-slate-700 hover:text-slate-900'}
                `}
              >
                <span className="flex items-center gap-2.5">
                  <span
                    className={`
                      flex h-9 w-9 items-center justify-center rounded-xl transition
                      ${openSource === 'url'
                        ? 'bg-gradient-to-br from-[#1a5276] to-[#123e5e] text-white shadow-[0_4px_12px_rgba(26,82,118,.28)]'
                        : 'bg-white text-slate-500 ring-1 ring-slate-200'}
                    `}
                  >
                    <LinkIcon size={15} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-bold leading-tight">Add by link</span>
                    <span className="block text-[11px] text-slate-500">
                      Paste a Drive / Dropbox / direct PDF URL — server fetches it
                    </span>
                  </span>
                </span>
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-slate-400 transition-transform ${openSource === 'url' ? 'rotate-180 text-[#1a5276]' : ''}`}
                />
              </button>
              {openSource === 'url' && (
                <div className="border-t border-slate-100 px-4 pb-4 pt-3">
                  <label className="mb-1.5 flex items-center gap-1 text-[10.5px] font-extrabold uppercase tracking-wider text-slate-500">
                    <LinkIcon size={11} className="text-[#1a5276]" />
                    PDF URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={urlInput}
                      onChange={(e) => { setUrlInput(e.target.value); setUrlError(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
                      placeholder="https://drive.google.com/… or any direct PDF link"
                      className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-900 outline-none focus:border-[#1a5276] focus:ring-2 focus:ring-[#1a5276]/15"
                    />
                    <button
                      type="button"
                      onClick={addUrl}
                      disabled={!urlInput.trim()}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#1a5276] px-3 py-2 text-[12.5px] font-bold text-white hover:bg-[#15436a] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Plus size={13} /> Add
                    </button>
                  </div>
                  {urlError && (
                    <p className="m-0 mt-1.5 flex items-center gap-1 text-[11px] text-[#c0392b]">
                      <AlertCircle size={11} /> {urlError}
                    </p>
                  )}
                  <p className="m-0 mt-1.5 text-[11px] text-slate-500">
                    The server fetches, validates and ingests the PDF for you — no download needed.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Pending items ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {pending.length === 0 ? (
            <div className="mt-8 flex flex-col items-center gap-2 text-center text-[13px] text-slate-500">
              <FileText size={22} className="text-slate-300" />
              <p className="m-0">Nothing queued yet.</p>
              <p className="m-0 max-w-[240px] text-[11.5px] text-slate-400">
                Drop a PDF into the box above or paste a link — items you queue land right here.
              </p>
            </div>
          ) : (
            <div className="grid gap-3.5">
              {pending.map((p) => {
                const isUrl = p.source === 'url';
                const canPreview = isUrl ? !!p.url : previewUrls.has(p.id);
                return (
                  <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
                    {/* File / URL header */}
                    <div className="flex items-center gap-3">
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                          isUrl ? 'bg-[#0e9172]/12 text-[#0e9172]' : 'bg-[#1a5276]/10 text-[#1a5276]'
                        }`}
                      >
                        {isUrl ? <LinkIcon size={15} /> : <FileText size={15} />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="m-0 truncate text-[13px] font-bold text-slate-900">
                          {p.display_name}
                        </p>
                        <p className="m-0 mt-0.5 truncate text-[11px] text-slate-500">
                          {isUrl
                            ? p.url
                            : `${((p.size_bytes ?? 0) / 1024 / 1024).toFixed(1)} MB · local file`}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => openPreview(p)}
                        disabled={!canPreview}
                        title="Preview in new tab"
                        className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 disabled:opacity-40"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemove(p.id)}
                        title="Remove"
                        className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {/* Metadata form */}
                    <div className="mt-3 grid gap-2.5">
                      {/* Kind toggle — Past paper (RAG corpus) vs Curriculum
                          (learning-outcome grounding for the generator).
                          Hidden when the parent page has already scoped
                          the drawer to a single kind. */}
                      {!lockedKind && (
                        <div className="flex overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-0.5">
                          {[
                            { key: 'exam_paper' as const, label: 'Past paper', hint: 'goes into the RAG corpus' },
                            { key: 'curriculum' as const, label: 'Curriculum', hint: 'trains the exam generator on learning outcomes' },
                          ].map((opt) => {
                            const on = (p.kind || 'exam_paper') === opt.key;
                            return (
                              <button
                                key={opt.key}
                                type="button"
                                onClick={() => onPatch(p.id, { kind: opt.key })}
                                className={`
                                  flex-1 rounded-lg px-3 py-2 text-[12px] font-bold transition
                                  ${on
                                    ? 'bg-white text-[#0e9172] shadow-[0_1px_2px_rgba(14,145,114,.14)]'
                                    : 'text-slate-500 hover:text-slate-800'}
                                `}
                                title={opt.hint}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <TaxonomyPicker
                        taxonomy={taxonomy}
                        loading={taxonomyLoading}
                        value={p}
                        onChange={(patch) => onPatch(p.id, patch)}
                        depth={(p.kind || 'exam_paper') === 'curriculum' ? 'level' : 'course'}
                      />
                      {(p.kind || 'exam_paper') === 'exam_paper' ? (
                        <>
                          <div className="grid grid-cols-2 gap-2">
                            <Field label="Year">
                              <input
                                type="text"
                                inputMode="numeric"
                                placeholder="2024"
                                value={p.year || ''}
                                onChange={(e) => onPatch(p.id, { year: e.target.value })}
                                className={inputClasses}
                              />
                            </Field>
                            <Field label="Paper type">
                              <select
                                value={p.paper_type || 'Final'}
                                onChange={(e) => onPatch(p.id, { paper_type: e.target.value as PaperType })}
                                className={selectClasses}
                              >
                                {PAPER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </Field>
                          </div>
                          <Field label="Academic session (optional)">
                            <input
                              type="text"
                              placeholder="e.g. May–Aug 2024"
                              value={p.academic_session || ''}
                              onChange={(e) => onPatch(p.id, { academic_session: e.target.value })}
                              className={inputClasses}
                            />
                          </Field>
                        </>
                      ) : (
                        <div className="rounded-xl border border-[#0e9172]/25 bg-[#0e9172]/5 p-3 text-[11.5px] text-slate-700">
                          <p className="m-0 font-bold text-[#0b6d56]">
                            Curriculum mode
                          </p>
                          <p className="m-0 mt-1 leading-relaxed">
                            The ingestion pipeline will parse this PDF and
                            extract its learning-outcome tree (LO codes,
                            performance criteria, topics). The exam generator
                            reads that tree at generation time so every
                            question tests a real outcome from your CDACC
                            unit standard.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Progress + error */}
                    {p.progress > 0 && p.progress < 100 && (
                      <div className="mt-2 h-1 overflow-hidden rounded-full bg-slate-200">
                        <span
                          className="block h-full rounded-full bg-gradient-to-r from-[#1a5276] to-[#0e9172] transition-all"
                          style={{ width: `${p.progress}%` }}
                        />
                      </div>
                    )}
                    {p.error && (
                      <p className="m-0 mt-2 flex items-center gap-1 text-[11px] text-[#c0392b]">
                        <AlertCircle size={11} /> {p.error}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Sticky actions ── */}
        <div
          className="flex gap-2 border-t border-slate-100 bg-white px-5 py-3"
          style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={onClear}
            disabled={pending.length === 0}
            className="flex-1 rounded-xl bg-slate-100 px-4 py-3 text-[13px] font-bold text-slate-700 hover:bg-slate-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#1a5276] px-4 py-3 text-[13px] font-bold text-white shadow-[0_6px_16px_rgba(26,82,118,0.25)] hover:bg-[#15436a] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            <FileCheck size={13} /> Upload {pending.length || ''}
          </button>
        </div>

        <style>{`
          @keyframes ecFade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes ecSlideInRight {
            from { transform: translateX(100%); opacity: .95; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>
      </aside>
    </div>,
    document.body,
  );
};

/* ── Local atoms ───────────────────────────────────────────── */
const inputClasses =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-900 outline-none focus:border-[#1a5276] focus:ring-2 focus:ring-[#1a5276]/15';

const selectClasses = inputClasses;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[10.5px] font-extrabold uppercase tracking-wider text-slate-500">
      {label}
    </span>
    {children}
  </label>
);
